import PageWrapper from "./PageWrapper";
import { ArrowHeadLeftIcon, ArrowHeadRightIcon, CloseIcon, TrendingIcon } from "../assets/icons";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import Modal from "../components/Modal";
import LoadingIcon from "../components/shared/LoadingIcon";
import { categories } from "../assets/categories";
import { FeedFilterOptions, useGetFollowingPostFeed, useGetPostFeed, useGetPostFeedOpen } from "../api/data-access";
import FeedCard from "../components/shared/cards/FeedCard";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { Categories, CollectionPeriods, DecoratedFeedListPostType, ListPostTypes, PagedResponse } from "../types/SharedTypes";
import TabBar from "../components/navigation/TabBar";
import Dropdown from "../components/shared/inputs/Dropdown";
import { collectionPeriods } from "../assets/general";

const Dashboard = ({auth}: {auth?: boolean}) => {
  
  const [feed, setFeed] = useState<'all' | 'following'>('all');
  
  if (auth === false) return (
    <PageWrapper>
      <InfinitePostFeed queryHook={useGetPostFeedOpen} />
    </PageWrapper>
  )
  return (
    <PageWrapper>
      <div className="escape-container">
        <TabBar removeBorder tabData={[
          {title: 'All', onClick: () => setFeed('all')},
          {title: 'Following', onClick: () => setFeed('following')}
        ]} />
      </div>
      {feed === 'following' 
        ? <InfinitePostFeed queryHook={useGetFollowingPostFeed} />
        : <InfinitePostFeed queryHook={useGetPostFeed} />
      }
    </PageWrapper>
  )
}

type InfinitePostFeedProps = {
  queryHook: (filter?: FeedFilterOptions, enabled?: boolean) => UseInfiniteQueryResult<InfiniteData<PagedResponse<DecoratedFeedListPostType>, unknown>, Error>;
}

const InfinitePostFeed = ({queryHook}: InfinitePostFeedProps) => {
  
  const [filters, setFilters] = useState<FeedFilterOptions>({
    category: undefined,
    type: undefined,
    period: undefined
  });
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  
  const {data: posts, isPending, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage} = queryHook(filters);
  
  const allData = posts ? posts.pages.flatMap((d) => d.data) : [];

  const listRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? allData.length + 1 : allData.length,
    estimateSize: () => 120,
    overscan: 1,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });
  
  useEffect(() => {
    const [lastItem] = [...virtualizer.getVirtualItems()].reverse();

    if (!lastItem) return;

    if (
      lastItem.index >= allData.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    allData.length,
    isFetchingNextPage,
    virtualizer.getVirtualItems(),
  ]);
  
  const virtualItems = virtualizer.getVirtualItems();
  
  if (isError) return <p>Error</p>;
  return (
    <>
      {filterModalVisible && 
        <FilterModal filters={filters} setFilters={setFilters} setModalVisible={setFilterModalVisible}  />
      }
      <div className="item-list">
        <div className="right-item-layout full-height center-horizontal no-gap align-content-start">
          <FilterButtons
            filters={categories}
            onClick={(val) => setFilters(prev => ({...prev, category: val as Categories || undefined}))}
            selected={filters.category || null}
            escapePadding
            gradientOverflow
          />
          <div className="height-80 item-row small">
            <div className="full-height left-border secondary-border" />
            <button className="icon xs" onClick={() => setFilterModalVisible(true)}>
              <ArrowHeadRightIcon />
            </button>
          </div>
        </div>
        {isPending && 
          <div className="item-row center-horizontal padding">
            <LoadingIcon />
          </div>
        }
        {isSuccess && 
          <section className="item-list large">
            {posts.pages.length === 0 && <p className="subtitle-large secondary center">Nothing to show.</p>}
            <div ref={listRef}>
              <div
                className="card-feed"
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${(virtualItems[0]?.start ?? 0) - virtualizer.options.scrollMargin}px)`,
                  }}
                >
                  {virtualItems.map((item) => {
                    const isLoaderRow = item.index > allData.length - 1;
                    const post = allData[item.index];
                    const dateSection = post ? post.dateSection === 'week' ? 'this week' : post.dateSection : undefined;

                    return (
                      <div
                        key={item.index}
                        data-index={item.index}
                        ref={virtualizer.measureElement}
                      >
                        {isLoaderRow && hasNextPage 
                          ?
                            <div className="item-row center-horizontal">
                              <LoadingIcon />
                            </div> 
                          :
                            <>
                              {dateSection && 
                                <p className="subtitle-medium center secondary padding-small-vertical header">{dateSection}</p>
                              }
                              <FeedCard
                                key={post._id}
                                post={post}
                                // badgeBanner={<TrendingBannerBadge/>}
                              />
                            </>
                        }
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </section>
        }
      </div>
    </>
  )
}

const TrendingBannerBadge = () => {
  return (
    <div className="item-row center-vertical">
      <div className="icon xsmall secondary">
        <TrendingIcon />
      </div>
      <p className="subtitle-medium secondary">Trending</p>
    </div>
  )
}

type Filter = {
  label: string;
  value: string;
}

type FilterButtonProps = {
  filters: Filter[];
  selected: string | null;
  escapePadding?: boolean;
  removePadding?: boolean;
  gradientOverflow?: boolean;
  wrap?: boolean;
  small?: boolean;
} & ({
  onClick: (val: string) => void;
  preventDeselect: true;
} | {
  onClick: (val: string | null) => void;
  preventDeselect?: never;
})

export const FilterButtons = ({filters, onClick, selected, escapePadding, removePadding, gradientOverflow, wrap, preventDeselect, small}: FilterButtonProps) => {
  
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const handleClick = (filter: Filter) => {
    preventDeselect
      ? onClick(filter.value) 
      : onClick(filter.value !== selected ? filter.value : null);
      
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo(0,0);
  }
  
  return (
    <div className="filter-buttons item-row" data-wrap={wrap} data-escape={escapePadding} data-no-padding={removePadding} data-gradient-overflow={gradientOverflow}>
      <div ref={scrollRef} className="filter-buttons-row item-row">
        {filters.map(filter =>
          <button
            key={filter.value}
            className={"filter secondary" + (small ? " small" : "")}
            aria-selected={filter.value === selected} 
            onClick={() => handleClick(filter)}
          >
            {filter.label}
          </button>
        )}
        {gradientOverflow && <div className="gradient-overflow" />}
      </div>
    </div>
  )
}

type FilterModalProps = {
  filters: FeedFilterOptions;
  setFilters: Dispatch<SetStateAction<FeedFilterOptions>>;
  setModalVisible: (prev: boolean) => void;
}

const typeOptions = [{label: "All", value: 'all'}, {label: "Lists", value: 'list'}, {label: "Collections", value: 'collection'}];
const periodOptions = [{label: "All", value: 'all'}, ...collectionPeriods];

export const FilterModal = ({filters, setFilters, setModalVisible}: FilterModalProps) => {

  const setTypeFilter = (val: string | undefined) => {
    setFilters(prev => ({...prev, type: val as ListPostTypes}));
  }
  const setPeriodFilter = (val: string | undefined) => {
    setFilters(prev => ({...prev, period: val as CollectionPeriods}));
  }
  const clearFilters = () => {
    setFilters({category: undefined, type: undefined, period: undefined});
  }

  return (
    <Modal fullscreen>
      <section className="item-list xl padding-small">
        <div className="item-row small">
          <button className="icon xs" onClick={() => setModalVisible(false)}>
            <ArrowHeadLeftIcon />
          </button>
          <p className="heading-small weight-bold">All Filters</p>
        </div>
        <div className="item-list large">
          <div className="horizontal-scroll-container end-spacer">
            <div className="item-row">
              {(filters.category || filters.type) && 
                <button className="icon background xs" onClick={clearFilters}>
                  <CloseIcon />
                </button>
              }
              <Dropdown
                value={typeOptions.find(t => t.value === filters.type) || null}
                options={typeOptions}
                onChange={(opt) => setTypeFilter(opt?.value === 'all' ? undefined : opt?.value)}
                error={false}
                placeholder={'Type'}
                isSearchable={false}
                classNames="fit-width bright"
                usePortal
              />
              <Dropdown
                value={periodOptions.find(t => t.value === filters.period) || null}
                options={periodOptions}
                onChange={(opt) => setPeriodFilter(opt?.value === 'all' ? undefined : opt?.value)}
                error={false}
                placeholder={'Period'}
                isSearchable={false}
                classNames="fit-width bright"
                usePortal
              />
            </div>
          </div>
          <p className="subtitle-large indent">Categories</p>
          <FilterButtons
            filters={categories}
            onClick={(val) => setFilters(prev => ({...prev, category: val as Categories || undefined}))}
            selected={filters.category || null}
            wrap
          />
        </div>
      </section>
    </Modal>
  )
}

export default Dashboard;